import { InviteCreateParams } from '@citruscamps/citrus-client'
import { useFormik } from 'formik'
import { default as React } from 'react'
import validator from 'validator'
import * as Yup from 'yup'
import { LoadingButton } from '../../../../../components/LoadingButton/LoadingButton'
import { Title } from '../../../../../components/Title/Title'
import translations from '../../../../../translations/en.json'

interface IProps {
  initialValues?: Partial<InviteCreateParams>
  onPrevious?: (invite: InviteCreateParams) => Promise<void>
  onSkip?: () => Promise<void>
  onSubmit: (invite: InviteCreateParams) => Promise<void>
}

export const InviteStep = ({
  initialValues,
  onPrevious,
  onSkip,
  onSubmit,
}: IProps): React.ReactElement => {
  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleBlur,
    handleChange,
    setFieldError,
    isSubmitting,
  } = useFormik<InviteCreateParams>({
    initialValues: {
      first_name: initialValues?.first_name || '',
      email: initialValues?.email || '',
      program_id: initialValues?.program_id || '',
      timezone: initialValues?.timezone || '',
    },
    validationSchema: Yup.object().shape({
      first_name: Yup.string().max(40, (translations as any)['error.first_name_maxlength']),
      email: Yup.string().test(
        'email',
        (translations as any)['error.email_invalid'],
        (val) => !val || validator.isEmail(val),
      ),
    }),
    onSubmit: async (value, { setSubmitting }) => {
      try {
        await onSubmit(value)
      } catch (e: any) {
        if (e.param) setFieldError(e.param, e.message)
      }
      setSubmitting(false)
    },
  })

  return (
    <form onSubmit={handleSubmit}>
      <Title value="Invite Team Members" />
      <h4 className="mb-0">Want to invite a team member right away?</h4>
      <p className="text-body-secondary d-block mb-4">You can skip this or do it later.</p>
      <div className="mb-3">
        <label className="form-label optional">Their first name</label>
        <input
          id="first_name"
          name="first_name"
          autoComplete="given-name"
          type="text"
          className={
            'form-control' + (touched.first_name && !!errors.first_name ? ' is-invalid' : '')
          }
          value={values.first_name}
          onBlur={handleBlur}
          onChange={handleChange}
          autoFocus
        />
        <div className="invalid-feedback d-block">
          {touched?.first_name && !!errors?.first_name ? errors?.first_name : '\u00A0'}
        </div>
      </div>
      <div className="mb-3">
        <label className="form-label optional">Their email address</label>
        <input
          id="email"
          name="email"
          autoComplete="email"
          type="email"
          className={'form-control' + (touched.email && !!errors.email ? ' is-invalid' : '')}
          value={values.email}
          onBlur={handleBlur}
          onChange={handleChange}
        />
        <div className="invalid-feedback d-block">
          {touched?.email && !!errors?.email ? errors?.email : '\u00A0'}
        </div>
      </div>
      <div className="d-flex justify-content-between mt-4">
        {onPrevious && (
          <button
            type="button"
            className="btn btn-link me-auto"
            disabled={isSubmitting}
            onClick={() => onPrevious(values)}
          >
            Back
          </button>
        )}
        {onSkip && (
          <button
            type="button"
            className="btn btn-link ms-auto me-3"
            disabled={isSubmitting}
            onClick={() => onSkip()}
          >
            Skip
          </button>
        )}
        <LoadingButton type="submit" className="btn btn-primary" loading={isSubmitting}>
          Next
        </LoadingButton>
      </div>
    </form>
  )
}
